import React, { FunctionComponent, useCallback } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useScrollCancelDrag } from '../../../../../../utils/useScrollCancelDrag';
import { INotesListProps } from '../types';

const PERSONAL_NOTES_LIST_DROPPABLE_ID = 'personal-notes-list';

export const NotesList: FunctionComponent<INotesListProps> = ({
  notes,
  selectedNoteId,
  onSelectNote,
  onReorder,
  isCollapsed = false,
}) => {
  const { sensors, handleDragStart, handleDragEnd } = useScrollCancelDrag();

  const handleDrop = useCallback(
    (result: DropResult) => {
      handleDragEnd();
      if (result.reason === 'CANCEL' || !result.destination) {
        return;
      }
      if (result.source.index === result.destination.index) {
        return;
      }

      const movedNoteId = notes[result.source.index].id;
      const noteIds = notes.map((n) => n.id);
      const reordered = [...noteIds];
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);
      onReorder(movedNoteId, reordered);
    },
    [handleDragEnd, notes, onReorder],
  );

  return (
    <div className={`personal-notes-list${isCollapsed ? ' personal-notes-list--collapsed' : ''}`}>
      <div className="personal-notes-list-items">
        {notes.length > 0 ? (
          <DragDropContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDrop}>
            <Droppable droppableId={PERSONAL_NOTES_LIST_DROPPABLE_ID} direction="vertical">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {notes.map((note, index) => (
                    <Draggable key={note.id} draggableId={note.id} index={index}>
                      {(draggableProvided) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          className={`personal-notes-list-item ${selectedNoteId === note.id ? 'active' : ''}`}
                          onClick={() => onSelectNote(note.id)}
                        >
                          <div className="personal-notes-list-item-title">{note.title || 'Untitled'}</div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="personal-notes-list-empty">
            <span className="blueGray">No notes yet</span>
          </div>
        )}
      </div>
    </div>
  );
};
